import { tableActionsCreator } from "app/actions/tableActions";
import { showSuccessToast } from "app/actions/toast_actions";
import { swSettings } from "app/common/services/swSettings";
import {
    AdsenseCell,
    CategoryFilterCell,
    IndexCell,
    RankCell,
    TrafficShare,
    WebsiteTooltipTopCell,
} from "app/components/React/Table/cells";
import { RowSelectionConsumer } from "app/components/React/Table/cells/RowSelection";
import { DefaultCellHeader, DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { SelectAllRowsHeaderCellConsumer } from "app/components/React/Table/headerCells/SelectAllRowsHeaderCell";
import { SWReactTableWrapperWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import { DomainSelection } from "app/components/React/TableSelectionComponents/DomainSelection";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { i18nFilter } from "app/filters/i18nFilter";
import { subCategoryFilter } from "app/filters/ngFilters";
import { CompetitiveLandscapeTableTop } from "app/pages/website-analysis/competitive-landscape/CompetitiveLandscapeTableTop";
import queryString from "query-string";
import type { FunctionComponent } from "react";
import { useState } from "react";
import { connect } from "react-redux";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { MAX_DOMAINS_IN_CATEGORY } from "app/components/customCategoriesWizard/custom-categories-max-domains";
import categoryService from "app/common/services/categoryService";
import { useCurrentStateAndParams } from "@uirouter/react";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { chosenSites } from "app/common/services/chosenSites";
import { QualificationSideBarHOC } from "app/components/QualificationSideBar/QualificationSideBarHOC";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";

const CompetitiveLandscapePage: FunctionComponent<any> = (props) => {
    const i18n = i18nFilter();
    const subCategory = subCategoryFilter();
    const downloadExcelPermitted = swSettings.current?.resources?.IsExcelAllowed;
    const [allCategories, setAllCategories] = useState([]);
    const isCompare = chosenSites.isCompare();
    const selectedSites = chosenSites.map((item, infoItem) => {
        return { value: item, label: infoItem.displayName, icon: infoItem.icon };
    });
    const { showToast, clearAllSelectedRows, selectedRows } = props;
    const {
        params: {
            key,
            country,
            isWWW,
            duration,
            webSource,
            similarsites_orderby,
            similarsites_filters,
            selectedSite: selectedSiteProps,
        },
        state,
    } = useCurrentStateAndParams();
    const handleOpenQualificationSideBar =
        !!state.qualificationSideBarComponent && props.handleOpenQualificationSideBar;
    const selectedSiteExists = selectedSiteProps ? selectedSites.find((s) => s.value === selectedSiteProps) : null;
    const selectedSite = selectedSiteExists ? selectedSiteExists.value : selectedSites[0].value;
    const selectedSiteLabel = selectedSiteExists ? selectedSiteExists.label : selectedSites[0].label;
    const filtersObject: any = {
        domain: {
            action: "contains",
        },
        category: {
            action: "category",
        },
    };
    const availableFilters: any = {};
    if (similarsites_filters) {
        similarsites_filters.split("+").forEach((filter) => {
            const values = filter.split(";");
            availableFilters[values[0]] = { ...filtersObject[values[0]], value: values[2] };
        });
    }
    // Convert filters to API params filter string.
    const filter = Object.keys(availableFilters)
        .map((filter) => {
            const { action, value } = availableFilters[filter];
            return [filter, action, `"${value}"`].join(";");
        })
        .join(",");
    const createFiltersForUrl = (filters) =>
        Object.keys(filters)
            .map((filter) => {
                const { action, value } = filters[filter];
                return [filter, action, value].join(";");
            })
            .join("+");
    let field = "Affinity";
    let sortDirection = "desc";
    if (similarsites_orderby) {
        [field, sortDirection] = similarsites_orderby.split(" ");
    }
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const params = {
        filter,
        country,
        from,
        includeSubDomains: isWWW === "*",
        isWindow,
        keys: isCompare ? selectedSite : key,
        orderBy: `${field} ${sortDirection}`,
        to,
        timeGranularity: "Monthly",
    } as any;
    const onSelectCategory = (value) => {
        let filters;
        if (!value) {
            const { category, ...restFilters } = availableFilters;
            filters = restFilters;
        } else {
            const category = { ...filtersObject.category };
            category.value = value;
            filters = { ...availableFilters, category };
        }
        proNavigator.updateParams({
            similarsites_filters: createFiltersForUrl(filters),
        });
    };
    const columns = [
        {
            fixed: true,
            cellComponent: RowSelectionConsumer,
            sortable: false,
            headerComponent: SelectAllRowsHeaderCellConsumer,
            isResizable: false,
            width: 40,
            visible: categoryService.hasCustomCategoriesPermission(),
            disableHeaderCellHover: true,
        },
        {
            fixed: true,
            cellComponent: IndexCell,
            disableHeaderCellHover: true,
            sortable: false,
            width: 40,
            isResizable: false,
        },
        {
            minWidth: 304,
            sortable: true,
            field: "Domain",
            showTotalCount: true,
            displayName: i18n("analysis.competitors.similar.table.columns.domain.title"),
            tooltip: i18n("analysis.competitors.similar.table.columns.domain.title.tooltip"),
            cellComponent: (props) => {
                return (
                    <WebsiteTooltipTopCell
                        {...props}
                        handleOpenQualificationSideBar={
                            state.qualificationSideBarComponent ? handleOpenQualificationSideBar : null
                        }
                    />
                );
            },
        },
        {
            minWidth: 304,
            sortable: true,
            field: "Category",
            displayName: i18n("analysis.competitors.similar.table.columns.category.title"),
            tooltip: i18n("analysis.competitors.similar.table.columns.category.title.tooltip"),
            cellComponent: (row) => {
                const onItemClick = () => onSelectCategory(row.value);
                return (
                    <CategoryFilterCell
                        {...row}
                        onItemClick={onItemClick}
                    />
                );
            },
        },
        {
            minWidth: 108,
            sortable: true,
            field: "Rank",
            displayName: i18n("analysis.competitors.similar.table.columns.rank.title"),
            tooltip: i18n("analysis.competitors.similar.table.columns.rank.title.tooltip"),
            cellComponent: RankCell,
            headerComponent: DefaultCellHeaderRightAlign,
            format: "rank",
        },
        {
            width: 180,
            sortable: true,
            field: "Affinity",
            displayName: i18n("analysis.competitors.similar.table.columns.share.title"),
            tooltip: i18n("analysis.competitors.similar.table.columns.share.title.tooltip", selectedSiteLabel),
            cellComponent: TrafficShare,
        },
        {
            width: 77,
            field: "HasAdsense",
            displayName: i18n("analysis.all.table.columns.googleAds.title"),
            tooltip: i18n("analysis.all.table.columns.googleAds.title.tooltip"),
            cellComponent: AdsenseCell,
        },
    ].map((col: any) => {
        const isSorted = col.field === field;
        return {
            ...col,
            visible: col.visible !== false,
            headerComponent: col.headerComponent || DefaultCellHeader,
            isSorted,
            sortDirection,
            isResizable: col.isResizable !== false,
        };
    });
    const getDataCallback = (data) => {
        const {
            Filters: { category },
        } = data;
        setAllCategories(category);
    };
    const onSort = ({ field, sortDirection }) => {
        proNavigator.updateParams({
            similarsites_orderby: `${field} ${sortDirection}`,
        });
    };
    const transformData = (data) => {
        const stateName = "digitalsuite_website_websiteperformance";
        return {
            ...data,
            Data: data.Data.map((record) => {
                return {
                    ...record,
                    url: proNavigator.href(stateName, {
                        ...proNavigator.getParams(),
                        key: record.Domain,
                    }),
                };
            }),
        };
    };
    const getExcelUrl = () => {
        const queryStringParams = queryString.stringify(params);
        return `/widgetApi/WebsiteCompetitors/SimilarSites/Excel?${queryStringParams}`;
    };
    const onSearchTermFilterChange = (value) => {
        let filters;
        if (value === "") {
            const { domain, ...restFilters } = availableFilters;
            filters = restFilters;
        } else {
            const domain = { ...filtersObject.domain };
            domain.value = value;
            filters = { ...availableFilters, domain };
        }
        proNavigator.updateParams({
            similarsites_filters: createFiltersForUrl(filters),
        });
    };
    const onClearAll = () => {
        proNavigator.updateParams({
            similarsites_filters: null,
        });
    };
    const onClearSite = () => {
        proNavigator.go(proNavigator.current().name, Object.assign(proNavigator.getParams(), { selectedSite: null }));
    };
    const onSelectSite = (selectedSite) => {
        proNavigator.go(
            proNavigator.current().name,
            Object.assign(proNavigator.getParams(), { selectedSite: selectedSite.text }),
        );
    };
    return (
        <SWReactTableWrapperWithSelection
            tableSelectionKey="CompetitiveLandscapeTable"
            tableSelectionProperty="Domain"
            maxSelectedRows={MAX_DOMAINS_IN_CATEGORY}
            cleanOnUnMount={true}
            tableOptions={{
                aboveHeaderComponents: [
                    <DomainSelection
                        key="DomainSelection"
                        showToast={showToast}
                        clearAllSelectedRows={clearAllSelectedRows}
                        selectedRows={selectedRows}
                    />,
                ],
                metric: "CompetitiveLandscapeTable",
                tableSelectionTrackingParam: "Domain",
                trackName: "Competitive Landscape",
            }}
            serverApi={`/widgetApi/WebsiteCompetitors/SimilarSites/Table`}
            tableColumns={columns}
            initialFilters={params}
            recordsField="Data"
            totalRecordsField="TotalCount"
            onSort={onSort}
            preventDefaultOnSort
            getDataCallback={getDataCallback}
            pageIndent={1}
            transformData={transformData}
        >
            {(topComponentProps) => (
                <CompetitiveLandscapeTableTop
                    {...topComponentProps}
                    selectedCategory={
                        availableFilters.category
                            ? decodeURIComponent(subCategory(availableFilters.category.value))
                            : null
                    }
                    selectedCategoryId={availableFilters.category?.value}
                    excelLink={getExcelUrl()}
                    searchValue={availableFilters.domain?.value}
                    downloadExcelPermitted={downloadExcelPermitted}
                    onSelectCategory={onSelectCategory}
                    allCategories={allCategories}
                    isCompare={isCompare}
                    selectedSite={selectedSite}
                    onChange={onSearchTermFilterChange}
                    onClearAll={onClearAll}
                    onSelectSite={onSelectSite}
                    onClearSite={onClearSite}
                    selectedSites={selectedSites}
                />
            )}
        </SWReactTableWrapperWithSelection>
    );
};

const mapStateToProps = (state) => {
    return {
        selectedRows: getTableSelectedItems("CompetitiveLandscapeTable")(state),
    };
};
const mapDispatchToProps = (dispatch) => {
    return {
        clearAllSelectedRows: () => {
            dispatch(tableActionsCreator("CompetitiveLandscapeTable", "Domain").clearAllSelectedRows());
        },
        showToast: (href, text, label) => {
            dispatch(
                showSuccessToast(
                    getToastItemComponent({
                        text,
                        linkText: label,
                        href,
                        onClick: () =>
                            allTrackers.trackEvent(
                                "add to Custom Category",
                                "click",
                                "internal link/websites.overview",
                            ),
                    }),
                ),
            );
        },
    };
};
export default connect(mapStateToProps, mapDispatchToProps)(QualificationSideBarHOC(CompetitiveLandscapePage));
